import type { Timestamp as FirestoreTimestamp } from '@angular/fire/firestore';
import type { ServiceId } from './service-id';

let Timestamp: typeof FirestoreTimestamp | { now: () => number; };
type Timestamp = FirestoreTimestamp;

if(typeof module != 'undefined' && module.exports) {
    // CommonJS environment
    Timestamp = {
        now: Date.now
    };
} else {
    // ES Module environment
    import('@angular/fire/firestore').then(firestore => {
        Timestamp = firestore.Timestamp;
    });
}

/**
 * List of generic statistic event types
 */
export enum StatTypeGeneric {
    ACCOUNT_CREATED = "ACCOUNT_CREATED",
    FIRST_PROJECT_VISIT = "FIRST_PROJECT_VISIT",
    MOTE_LOVED = "MOTE_LOVED",
    TOUR_OPENED = "TOUR_OPENED",
    MOTE_OPENED = "MOTE_OPENED",
    QUIZ_ANSWER_WRONG = "QUIZ_ANSWER_WRONG",
    QUIZ_ANSWER_RIGHT = "QUIZ_ANSWER_RIGHT",
    USER_LOGIN = "USER_LOGIN",
    USER_LOGIN_ERROR = "USER_LOGIN_ERROR",
    USER_LOGOUT = "USER_LOGOUT"
}

/**
 * List of statistic event types with additional event specific data
 */
export enum StatTypeExtended {
    USER_CREATED = "USER_CREATED",
    LANDING_VISITED = "LANDING_VISITED",
    DIRECTIONS_CLICKED = "DIRECTIONS_CLICKED",
    JOURNEY_LOADED = "JOURNEY_LOADED",
    SEARCH_QUERY = "SEARCH_QUERY",
    SEARCH_RESULT_SELECTED = "SEARCH_RESULT_SELECTED",
    USER_SURVEY_RESPONSE = "USER_SURVEY_RESPONSE", // Deprecated
    TOUR_REVIEWED = "TOUR_REVIEWED",
    USER_COMMENT = "USER_COMMENT",
    VOUCHER_PURCHASED = "VOUCHER_PURCHASED",
    VOUCHER_CLAIMED = "VOUCHER_CLAIMED",
    VOUCHER_REDEEMED = "VOUCHER_REDEEMED",
    MEDIA_CONSUMED = "MEDIA_CONSUMED",
    CREATOR_SIGNUP = "CREATOR_SIGNUP",
    TOUR_PAGE_VISITED = "TOUR_PAGE_VISITED",
    FIRST_UNLOCKED = "FIRST_UNLOCKED",
    MOTE_UNLOCKED = "MOTE_UNLOCKED",
    TOUR_STARTED = "TOUR_STARTED",
    TOUR_COMPLETED = "TOUR_COMPLETED",
    /** Unlock on another tour's story while the user's session is attributed to this tour (same addition shape as MOTE_UNLOCKED) */
    EXTRA_MOTE_UNLOCKED = "EXTRA_MOTE_UNLOCKED",
}

// Create merged enum (define value and named type)
export const StatType = Object.assign({}, StatTypeGeneric, StatTypeExtended);
export type StatType = StatTypeGeneric | StatTypeExtended;

export class StatEventJson<DateType=Timestamp | number> {
    /** A unique database id/key of the event */
    public id?: string;
    /** The type of the event */
    public statType: StatType;
    /** Timestamp representing the server time when the event was generated */
    public createdOn?: DateType;
    /** A key string with a unique key generated on the device (phone, tablet, pv etc.) from where the event originated */
    public deviceKey?: string;
    /** Represents the users admin permission state when the event occurred */
    public isAdmin?: boolean;
    /** The GPS coordinates (latitude, longitude) where the event was generated */
    public position?: { lat: number, long: number };
    /** A unique key representing a visitor session - reset on reload (which is not directly related to the user as to comply with GDPR) */
    public visitKey?: string;
    /** A unique key representing a visitor across devices and domains */
    public visitorKey?: string;
    /** Timestamp representing when the visitor initiated the current session */
    public visitStartTime?: DateType;
    /** A unique key representing the project/world that was active at the time when the event occurred */
    public projectKey?: string;
    /** A unique key representing the id of the mote associated with the event */
    public moteKey?: string;
    /** Tour key aka. mote parent */
    public tourKey?: string;
    /** The number of times the user has previously visited the site */
    public visitCount?: number = 0;
    /** URI encoded referer from browser indicating which url the user was referred through */
    public referer?: string;
    /** String representing the language version reported by browser as defined in the BCP 47 standard */
    public language?: string;
    /** User agent reported by the browser on the device ie. '"Mozilla/5.0 (Linux; Android 7.0; SM-G920F)...' */
    public userAgent?: string;
    /** Additional statistic event information provided by specific sub classes */
    public addition?;
    /** The service from which the event is send */
    public serviceId?: ServiceId;
}

export class StatEventBase extends StatEventJson<Date> {
    constructor(json: StatEventJson<number>) {
        super();
        if(json) this.fromJson(json);
    }

    public toJson(): object {
        let ev: StatEventJson = { statType: this.statType };

        Object.assign(ev, this);

        // Delete empty fiels (do not store this overhead in all events)
        for(let propName in ev) {
            if(typeof ev[propName] == 'undefined' || ev[propName] == null) {
                delete ev[propName];
            }
        }
        // Convert createdOn date to number
        if(ev.createdOn instanceof Date) ev.createdOn = ev.createdOn.getTime();
        // Set server time if createdOn was not set yet
        if(!ev.createdOn) ev.createdOn = Timestamp?.now() || Date.now();

        // Convert visitStartTime date to number
        if(ev.visitStartTime instanceof Date) ev.visitStartTime = ev.visitStartTime.getTime();
        // Set server time if visitStartTime was not set yet
        if(!ev.visitStartTime) ev.visitStartTime = Timestamp?.now() || Date.now();
        return ev;
    }

    public fromJson(json: StatEventJson<number>): StatEventBase {
        this.id = json.id;
        this.statType = json.statType as any;
        this.createdOn = StatEventBase.firestoreTimestampToDate(json.createdOn);
        this.visitKey = json.visitKey;
        this.visitorKey = json.visitorKey;
        this.deviceKey = json.deviceKey;
        this.visitCount = json.visitCount;
        this.visitStartTime = StatEventBase.firestoreTimestampToDate(json.visitStartTime);
        this.projectKey = json.projectKey;
        this.position = json.position;
        this.moteKey = json.moteKey;
        this.tourKey = json.tourKey;
        this.isAdmin = json.isAdmin;
        this.addition = json.addition;
        this.serviceId = json.serviceId;
        this.referer = json.referer;
        this.language = json.language;
        this.userAgent = json.userAgent;
        return this;
    }

    private static firestoreTimestampToDate(timestamp: number | {seconds: number, nanoseconds: number}) : Date {
        if(!timestamp) return null;
        if(typeof timestamp == 'number') return new Date(timestamp);
        if(typeof timestamp == 'object' && timestamp.seconds) return new Date(timestamp.seconds * 1000);
        return null;
    }
}
